import useFetchApi, { isLoading } from 'hooks/useFetchApi/useFetchApi'
import { IOption } from 'interfaces'
import { groupBy, isEmpty, isUndefined, keys } from 'lodash-es'
import {
    IngredientContract,
    TaxContract,
    UOMContract,
    VendorContract,
    ItemContract,
} from 'models'
import { useCallback, useEffect, useMemo } from 'react'
import ingredientService from 'Services/api/ingredient.service'
import locationService from 'Services/api/location.service'
import vendorService from 'Services/api/vendor.service'
import useCommonProductEditDependencies from 'views/Products/hooks/useCommonProductEditDependencies'

export type TIngredientOption = {
    ingredientID: string
    name: string
    stockUnits: number
    uomid: number
    cost: number
    isAvailableForSelection: boolean
    enableTrackInventory: boolean
}

const ingredientToOption = (
    ingredient: IngredientContract,
): TIngredientOption => ({
    ingredientID: ingredient.itemID!,
    name: ingredient.name!,
    stockUnits: ingredient.locationItems![0]!.stockUnits!,
    uomid: ingredient.uomid!,
    cost: ingredient.locationItems![0]!.cost!,
    isAvailableForSelection: ingredient.locationItems![0]!
        .isAvailableForSelection!,
    enableTrackInventory: ingredient.enableTrackInventory!,
})

export const uomToOption = (item: UOMContract) =>
    ({
        label: `${item.symbol} ${item.name ? ` (${item.name})` : ''}`,
        value: item.uomid,
    } as IOption)

export const taxToOption = (item: TaxContract) => ({
    value: item.taxID,
    label:
        `${item.name}: ${item.rate?.toFixed(2)}%` +
        (item.taxComponents && item.taxComponents.length > 0
            ? ` (${item.taxComponents
                  .map((t) => `${t.name} ${t.rate?.toFixed(2)}%`)
                  .join(', ')})`
            : ''),
})

const vendorToOption = (item: VendorContract) => ({
    value: item.vendorID,
    label: item.name,
})

const useGetProductAndDependencies = (
    productID?: string,
    onAllowLocationData?: (itemId: string) => void,
) => {
    const {
        loading: commonDependencyLoading,
        uomLists,
        uoms,
        product,
        glcodes,
        productCategories,
        locations,
        initLocationData: initLocationDataApi,
        initLocationDataStatus,
        duplicatedNameList,
        addDuplicatedName,
        fetchUomStatus,
        taxes,
        fetchTaxesStatus,
    } = useCommonProductEditDependencies(productID)

    const [
        locationSetting,
        fetchLocationSetting,
        locationSettingStatus,
    ] = useFetchApi(locationService.getLocationSettingOfCurrentUser)

    const [vendors = [], fetchVendors, getVendorsStatus] = useFetchApi(
        vendorService.getVendors,
    )

    const [
        ingredients = [],
        fetchIngredient,
        fetchIngredientStatus,
    ] = useFetchApi(ingredientService.getIngredients)

    const initLocationData = useCallback(async () => {
        if (productID) {
            try {
                await initLocationDataApi()
                onAllowLocationData && onAllowLocationData(productID)
            } catch (err) {
                throw err
            }
        }
    }, [initLocationDataApi, productID, onAllowLocationData])

    const defaultProduct = useMemo<ItemContract>(() => {
        return product
            ? {
                  ...product,
              }
            : {
                  name: '',
                  description: '',
                  barcode: '',
                  uomid: 1,
                  isActive: true,
                  locationItems: [
                      {
                          taxID:
                              taxes.length === 1
                                  ? taxes.map(taxToOption)[0].value
                                  : '',
                          isTaxInclusive: true,
                      },
                  ],
              }
    }, [product, taxes])

    // Get Ingredient item IDs which use current item as ingredients
    const getUsedAsIngredientItemIDs = useCallback(
        // ItemID : Current Node
        (itemID: string) => {
            var itemIngredient = ingredients.find((i) => i.itemID === itemID)
            if (
                // Not found this item aka this item does not be used as an ingredient
                isUndefined(itemIngredient)
            )
                return []

            var itemIDs = [itemID]
            if (
                // No upper nodes aka current ingredients have not been used for others
                isEmpty(itemIngredient.elementOfItemUnits ?? [])
            )
                return itemIDs

            var elementOfItemUnits = itemIngredient.elementOfItemUnits!
            elementOfItemUnits.forEach((elementItem) => {
                itemIDs = [
                    ...itemIDs,
                    ...getUsedAsIngredientItemIDs(elementItem.itemID!),
                ]
            })
            // check duplicated item
            // ex: A is an ingredient for B and C bu B also is an ingredient for C
            // -> Final list = [A, B, C, C] -> Distinct = [A, B, C]
            return keys(groupBy(itemIDs))
        },
        [ingredients],
    )

    const filterIngredients = useMemo(() => {
        if (product && product.isUseAsIngredient) {
            var itemIDs = [
                product.itemID!,
                ...(product.itemVariations?.flatMap((iv) => iv.itemID!) ?? []),
            ]

            // Do not check product's item units
            // -> must be available to be use as ingredient from the beginning
            // If these items are not in the ingredient list -> error
            var currentIngredientIDs = (product.itemUnits ?? [])
                .concat(
                    product.itemVariations?.flatMap(
                        (iv) => iv.itemUnits ?? [],
                    ) ?? [],
                )
                .flatMap((i) => i.elementItemID!)

            var currentUsedAsIngredientIDs = (product.elementOfItemUnits ?? [])
                .concat(
                    product.itemVariations?.flatMap(
                        (iv) => iv.elementOfItemUnits ?? [],
                    ) ?? [],
                )
                .flatMap((i) => i.itemID!)

            // Get all ingredient item IDs which use items that use current product as an ingredient
            var allUsedAsIngredientIDs = keys(
                groupBy(
                    currentUsedAsIngredientIDs
                        .map((id) => getUsedAsIngredientItemIDs(id))
                        .flat(),
                ),
            )

            // Not allow to used itself as an ingredient
            // Not allow using-this-product-as-ingredient items to be this product ingredients
            var forbiddenItemIDs = itemIDs.concat(allUsedAsIngredientIDs)
            var availableIngredients = ingredients.filter(
                (i) =>
                    !forbiddenItemIDs.includes(i.itemID!) ||
                    // current ingredients must not be forbidden
                    currentIngredientIDs.includes(i.itemID!),
            )
            return availableIngredients
        } else return ingredients
    }, [product, ingredients, getUsedAsIngredientItemIDs])

    const elementOfItemUnits = useMemo(
        () => product?.elementOfItemUnits ?? [],
        [product],
    )

    const modifierOptions = useMemo(() => product?.modifierOptions ?? [], [
        product,
    ])

    useEffect(() => {
        fetchIngredient(true)
        fetchVendors()
        fetchLocationSetting()
    }, [fetchIngredient, fetchVendors, fetchLocationSetting])

    return {
        loading:
            commonDependencyLoading ||
            isLoading([
                fetchTaxesStatus,
                fetchIngredientStatus,
                getVendorsStatus,
                initLocationDataStatus,
                locationSettingStatus,
            ]),
        uomLists,
        taxLists: taxes.map(taxToOption),
        taxes: taxes,
        product: defaultProduct,
        glcodes,
        uoms,
        fetchUomStatus,
        productCategories,
        ingredients: filterIngredients.map(ingredientToOption),
        vendors: vendors.map(vendorToOption),
        initLocationData,
        companyTaxInclusiveSetting:
            locationSetting?.companyProfile?.isTaxInclusive ?? false,
        locations,
        elementOfItemUnits,
        modifierOptions,
        duplicatedNameList,
        addDuplicatedName,
    }
}

export default useGetProductAndDependencies
